import axios, { AxiosError } from 'axios';
import { featureAnalytics } from './featureAnalytics';
import { singleChapterService } from './singleChapterService';

const API_BASE_URL = 'https://bible-api.com';
const MAX_RETRIES = 3;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const SEARCH_CACHE_DURATION = 1000 * 60 * 30; // 30 minutes for search results

export interface BibleVersion {
  id: string;
  name: string;
  fullName: string;
  language: string;
  year?: number;
  license: string;
  available: boolean;
}

export interface BiblePassage {
  id: string;
  reference: string;
  text: string;
  version: string;
  book: string;
  chapter: number;
  verse?: number;
  verses?: BiblePassage[];
}

export interface BibleChapter {
  id: string;
  reference: string;
  verses: BiblePassage[];
  copyright?: string;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface SearchResult {
  query: string;
  verses: Array<{
    id: string;
    reference: string;
    text: string;
    relevance: number;
  }>;
  total: number;
}

class BibleService {
  private axiosInstance;
  private memoryCache: Map<string, CacheEntry<any>>;
  private offlineMode: boolean;

  // Maximum chapter counts for each book
  private readonly BOOK_CHAPTER_COUNTS: { [key: string]: number } = {
    'genesis': 50, 'exodus': 40, 'leviticus': 27, 'numbers': 36, 'deuteronomy': 34,
    'joshua': 24, 'judges': 21, 'ruth': 4, '1-samuel': 31, '2-samuel': 24,
    '1-kings': 22, '2-kings': 25, '1-chronicles': 29, '2-chronicles': 36, 'ezra': 10,
    'nehemiah': 13, 'esther': 10, 'job': 42, 'psalms': 150, 'proverbs': 31,
    'ecclesiastes': 12, 'song-of-solomon': 8, 'isaiah': 66, 'jeremiah': 52, 'lamentations': 5,
    'ezekiel': 48, 'daniel': 12, 'hosea': 14, 'joel': 3, 'amos': 9,
    'obadiah': 1, 'jonah': 4, 'micah': 7, 'nahum': 3, 'habakkuk': 3,
    'zephaniah': 3, 'haggai': 2, 'zechariah': 14, 'malachi': 4,
    'matthew': 28, 'mark': 16, 'luke': 24, 'john': 21, 'acts': 28,
    'romans': 16, '1-corinthians': 16, '2-corinthians': 13, 'galatians': 6, 'ephesians': 6,
    'philippians': 4, 'colossians': 4, '1-thessalonians': 5, '2-thessalonians': 3, '1-timothy': 6,
    '2-timothy': 4, 'titus': 3, 'philemon': 1, 'hebrews': 13, 'james': 5,
    '1-peter': 5, '2-peter': 3, '1-john': 5, '2-john': 1, '3-john': 1,
    'jude': 1, 'revelation': 22
  };

  private getValidChapterNumber(book: string, requestedChapter: number): number {
    const normalizedBook = book.toLowerCase().replace(/\s+/g, '-');
    const maxChapters = this.BOOK_CHAPTER_COUNTS[normalizedBook] || 1;
    
    if (requestedChapter < 1 || requestedChapter > maxChapters) {
      console.warn(`Chapter ${requestedChapter} does not exist in ${book}. Defaulting to chapter 1.`);
      return 1;
    }
    
    return requestedChapter;
  }

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // Increased to 30 second timeout
    });

    this.memoryCache = new Map();
    this.offlineMode = false;

    // Add response interceptor for error handling and retries
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (!error.config) throw error;

        // Check for network connectivity issues
        if (!navigator.onLine || error.message === 'Network Error') {
          this.offlineMode = true;
          throw new Error('No internet connection. Using offline cache if available.');
        }

        const retryCount = (error.config as any).__retryCount || 0;
        if (retryCount >= MAX_RETRIES) throw error;

        // Increment retry count
        (error.config as any).__retryCount = retryCount + 1;

        // Exponential backoff
        const backoffTime = Math.min(1000 * (2 ** retryCount), 10000);
        await new Promise(resolve => setTimeout(resolve, backoffTime));

        return this.axiosInstance(error.config);
      }
    );

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.offlineMode = false;
      featureAnalytics.trackEvent('bibleService', 'status', { status: 'online' });
    });
    
    window.addEventListener('offline', () => {
      this.offlineMode = true;
      featureAnalytics.trackEvent('bibleService', 'status', { status: 'offline' });
    });
  }

  private getCacheKey(key: string): string {
    return `bible_${key}`;
  }

  private isCacheValid<T>(entry: CacheEntry<T> | undefined, duration: number): boolean {
    if (!entry) return false;
    return Date.now() - entry.timestamp < duration;
  }

  private async getFromCacheOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    duration: number = CACHE_DURATION
  ): Promise<T> {
    const cacheKey = this.getCacheKey(key);
    const cachedData = this.memoryCache.get(cacheKey);

    if (this.isCacheValid(cachedData, duration)) {
      return cachedData.data;
    }

    try {
      const data = await fetchFn();
      this.memoryCache.set(cacheKey, { data, timestamp: Date.now() });
      return data;
    } catch (error) {
      // If primary API fails, try fallback
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        try {
          // Switch to fallback API
          this.axiosInstance.defaults.baseURL = API_BASE_URL;
          const data = await fetchFn();
          this.memoryCache.set(cacheKey, { data, timestamp: Date.now() });
          return data;
        } catch (fallbackError) {
          // If both APIs fail but we have cache, use it regardless of age
          if (cachedData) {
            console.warn('Using expired cache due to API failure');
            return cachedData.data;
          }
          throw fallbackError;
        }
      }
      
      // For other errors, use cache if available
      if (cachedData) {
        console.warn('Using cached data due to fetch error');
        return cachedData.data;
      }
      throw error;
    }
  }

  async getPassage(version: string = 'kjv', reference: string): Promise<BiblePassage> {
    const cacheKey = `passage_${version}_${reference}`;
    
    return this.getFromCacheOrFetch(
      cacheKey,
      async () => {
        try {
          // Parse the reference to get book and chapter
          const match = reference.match(/^([\w\s-]+?)\s*(\d+)(?::(\d+))?$/);
          if (!match) {
            throw new Error(`Invalid reference format: ${reference}`);
          }

          const [, book, chapter, verse] = match;
          const normalizedBook = book.toLowerCase().trim().replace(/\s+/g, '-');

          // Check if this is a single chapter book
          const isSingleChapter = normalizedBook === 'jude' || 
                                normalizedBook === 'philemon' ||
                                normalizedBook === '2-john' ||
                                normalizedBook === '3-john' ||
                                normalizedBook === 'obadiah';

          if (isSingleChapter) {
            // For single chapter books, use singleChapterService
            const verses = await singleChapterService.getVerses(book, version);

            // If a specific verse was requested (e.g., "Jude 1:5"), return just that verse
            if (verse) {
              const verseNumber = parseInt(verse);
              const verseData = verses.find(v => v.verse_number === verseNumber);
              
              if (!verseData) {
                throw new Error(`Verse ${verseNumber} not found in ${book}`);
              }

              return {
                id: `${book}-1-${verseNumber}`,
                reference: `${book} 1:${verseNumber}`,
                text: verseData.text,
                version: version,
                book: book,
                chapter: 1,
                verse: verseNumber,
                verses: [{
                  id: `${book}-1-${verseNumber}`,
                  reference: `${book} 1:${verseNumber}`,
                  text: verseData.text,
                  version: version,
                  book: book,
                  chapter: 1,
                  verse: verseNumber
                }]
              };
            }

            // If no specific verse was requested, return all verses
            const formattedVerses = verses.map(v => ({
              id: `${book}-1-${v.verse_number}`,
              reference: `${book} 1:${v.verse_number}`,
              text: v.text,
              version: version,
              book: book,
              chapter: 1,
              verse: v.verse_number
            }));

            return {
              id: `${book}-1`,
              reference: `${book} 1`,
              text: verses.map(v => v.text).join(' '),
              version: version,
              book: book,
              chapter: 1,
              verses: formattedVerses
            };
          }

          // For regular books, validate the chapter number
          const validChapter = this.getValidChapterNumber(book, parseInt(chapter));
          
          // Format the reference properly for the API
          // The API expects: /BookName+Chapter (e.g., /Genesis+1)
          const formattedBook = book.trim().replace(/\s+/g, ''); // Remove all spaces from book name
          let formattedRef = `${formattedBook}+${validChapter}`;
          if (verse) {
            formattedRef += `:${verse}`;
          }

          // Use the Bible API with the validated chapter number
          const response = await this.axiosInstance.get(`/${formattedRef}`, {
            params: {
              translation: version
            }
          });

          // Ensure the version and chapter are correctly set in the response
          return {
            ...response.data,
            version,
            chapter: validChapter,
            verses: response.data.verses || []
          };
        } catch (error) {
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 404) {
              // Try to provide a more helpful error message
              const message = `The passage "${reference}" could not be found in the ${version.toUpperCase()} translation. `
                + `Please check the reference and translation.`;
              throw new Error(message);
            }
            throw new Error(`Unable to load Bible passage: ${error.response?.data?.message || error.message}`);
          }
          throw error;
        }
      },
      CACHE_DURATION  // Use standard cache duration
    );
  }

  async getChapter(version: string, book: string, chapter: number): Promise<BibleChapter> {
    // Validate and adjust chapter number if needed
    const validChapter = this.getValidChapterNumber(book, chapter);
    const cacheKey = `chapter_${version}_${book}_${validChapter}`;
    
    return this.getFromCacheOrFetch(
      cacheKey,
      async () => {
        try {
          // Normalize the book name for comparison
          const normalizedBook = book.toLowerCase().replace(/\s+/g, '-');

          // Check if this is a single chapter book
          const isSingleChapter = normalizedBook === 'jude' || 
                                normalizedBook === 'philemon' ||
                                normalizedBook === '2-john' ||
                                normalizedBook === '3-john' ||
                                normalizedBook === 'obadiah';

          if (isSingleChapter) {
            // For single chapter books, use singleChapterService
            const verses = await singleChapterService.getVerses(book, version);

            const chapterData = {
              id: `${book}-1`,
              reference: `${book} 1`,
              verses: verses.map(v => ({
                id: `${book}-1-${v.verse_number}`,
                reference: `${book} 1:${v.verse_number}`,
                text: v.text,
                version: version,
                book: book,
                chapter: 1,
                verse: v.verse_number
              })),
              copyright: ''
            };

            return chapterData;
          }

          // For regular books, use the Bible API
          const formattedRef = `${book}+${validChapter}`;
          const response = await this.axiosInstance.get(`/${formattedRef}`, {
            params: {
              translation: version
            }
          });

          if (!response.data || !response.data.verses) {
            console.error('No verses in Bible API response');
            throw new Error(`No verses found for ${book} ${validChapter}`);
          }

          const chapterData = {
            id: `${book}-${validChapter}`,
            reference: `${book} ${validChapter}`,
            verses: response.data.verses.map(v => ({
              id: `${book}-${validChapter}-${v.verse}`,
              reference: `${book} ${validChapter}:${v.verse}`,
              text: v.text,
              version: version,
              book: book,
              chapter: validChapter,
              verse: v.verse
            })),
            copyright: response.data.copyright
          };

          return chapterData;
        } catch (error) {
          console.error('Error in getChapter:', error);
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 404) {
              throw new Error(`Chapter not found: ${book} ${validChapter}`);
            }
            throw new Error(`API Error: ${error.response?.data?.message || error.message}`);
          }
          throw new Error(`Failed to fetch chapter: ${error.message}`);
        }
      },
      CACHE_DURATION
    );
  }

  async searchVerses(version: string = 'kjv', query: string): Promise<SearchResult> {
    const cacheKey = `search_${version}_${query}`;
    const cachedResults = this.memoryCache.get(this.getCacheKey(cacheKey));

    if (this.isCacheValid(cachedResults, SEARCH_CACHE_DURATION)) {
      return cachedResults.data;
    }

    try {
      const params = new URLSearchParams();
      if (version && version !== 'kjv') {
        params.append('translation', version);
      }
      params.append('query', query);

      const response = await this.axiosInstance.get(`/search?${params.toString()}`);
      const results = {
        query,
        verses: response.data.data,
        total: response.data.total
      };

      this.memoryCache.set(this.getCacheKey(cacheKey), { data: results, timestamp: Date.now() });
      return results;
    } catch (error) {
      console.error('Error in searchVerses:', error);
      throw new Error('Failed to search Bible verses. Please try again.');
    }
  }

  clearCache(): void {
    this.memoryCache.clear();
    featureAnalytics.trackEvent('bibleService', 'cache', { action: 'clear' });
  }

  private getCached<T>(key: string, forceOffline: boolean = false): T | undefined {
    const cacheKey = this.getCacheKey(key);
    const cachedData = this.memoryCache.get(cacheKey);

    if (forceOffline || this.isCacheValid(cachedData, CACHE_DURATION)) {
      return cachedData?.data;
    }
    return undefined;
  }

  private setCache<T>(key: string, data: T): void {
    const cacheKey = this.getCacheKey(key);
    this.memoryCache.set(cacheKey, { data, timestamp: Date.now() });
  }
}

export const bibleService = new BibleService();
